/**
 * Utility for fetching and saving PDE JSON files in Firebase Cloud Storage.
 */
import { getDownloadUrl, invalidateDownloadUrlCache } from "@/data/repository/FirebaseImageRepository";
import { storage } from "@/main";

/** Resolves the download URL for a path and fetches + parses the JSON. */
async function fetchJsonFromStorage(storageLocation: string): Promise<Record<string, unknown>> {
  const downloadUrl = await getDownloadUrl(storageLocation);
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDE file: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetches and parses a PDE JSON file from Cloud Storage.
 *
 * The download URL is cached per path (see {@link getDownloadUrl}). When the
 * file is overwritten (by another user's save or by the ERP), Storage rotates
 * the file's download token and the cached URL becomes invalid, so the first
 * fetch can fail with a 403. If it does, the stale cached URL is invalidated
 * and the fetch is retried once with a freshly resolved (new-token) URL.
 *
 * @param storageLocation The Firebase Storage path (from Report.jsonDataStorageLocation)
 * @returns The parsed PDE JSON object
 */
export async function fetchPdeJson(storageLocation: string): Promise<Record<string, unknown>> {
  try {
    return await fetchJsonFromStorage(storageLocation);
  } catch {
    invalidateDownloadUrlCache(storageLocation);
    return await fetchJsonFromStorage(storageLocation);
  }
}

/**
 * Writes an updated PDE JSON document back to Cloud Storage, replacing the
 * existing file at the same path (same location, same name).
 *
 * Used by the PDE Viewer's save action when an IRES admin edits ERP-editable
 * fields inline. The cached download URL for the path is invalidated so any
 * subsequent read/download fetches the freshly written file.
 *
 * @param storageLocation The Firebase Storage path to overwrite
 * @param data The full PDE document to serialize and store
 */
export async function savePdeJson(
  storageLocation: string,
  data: Record<string, unknown>,
): Promise<void> {
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
  await storage.ref(storageLocation).put(blob);
  invalidateDownloadUrlCache(storageLocation);
}
